import { useState } from 'react';
import { Link } from 'react-router';
import { Camera, ChevronDown, ChevronRight, Eye, Undo2 } from 'lucide-react';
import {
  type CMSnapshotDevice,
  type RollbackDevicePreview,
  useCMRestores,
  useCMSnapshots,
  usePreviewRollback,
  useRollbackCM,
} from '@/hooks/useChangeManagements';
import { RestoreStatusBadge, RestoreTimes } from '@/components/ConfigRestoreHistory';
import { apiErrorMessage } from '@/utils/apiError';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AppSheet } from '@/components/ui/app-sheet';
import ConfirmationModal from '@/components/ConfirmationModal';
import { cn } from '@/lib/utils';
import ConfigText from '@/components/ConfigText';
import { RestoreWarnings } from '@/components/RestoreWarnings';

interface CMSnapshotsPanelProps {
  cmId: string;
  /** Whether to offer the "Roll back to baseline" action (a pre snapshot exists). */
  canRollback?: boolean;
}

function statusBadge(d: CMSnapshotDevice): { label: string; className: string } {
  if (!d.pre && !d.post) return { label: 'No snapshot', className: 'bg-muted text-muted-foreground' };
  if (d.pre && !d.post) return { label: 'Baseline only', className: 'bg-sev-5/15 text-sev-5' };
  if (d.changed) return { label: 'Changed', className: 'bg-sev-2/15 text-sev-2' };
  return { label: 'Unchanged', className: 'bg-sev-4/15 text-sev-4' };
}

/** Before/after config snapshots captured for the change's affected devices. */
export default function CMSnapshotsPanel({ cmId, canRollback = false }: CMSnapshotsPanelProps) {
  const { data: snapshots } = useCMSnapshots(cmId);
  const { data: restores } = useCMRestores(cmId);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [confirmRollback, setConfirmRollback] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState<RollbackDevicePreview[] | null>(null);
  const rollback = useRollbackCM();
  const previewRollback = usePreviewRollback();

  const runPreview = () => {
    setPreview(null);
    setPreviewOpen(true);
    previewRollback.mutate(cmId, {
      onSuccess: (devices) => { setPreview(devices); },
    });
  };

  if (!snapshots || snapshots.length === 0) return null;

  const hasBaseline = snapshots.some((d) => d.pre != null);

  return (
    <section className="bg-card border border-border rounded-lg p-5">
      <div className="flex items-start justify-between gap-3 mb-1">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <Camera className="w-4 h-4" />
          Config snapshots ({snapshots.length})
        </h2>
        {canRollback && hasBaseline ? (
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              disabled={previewRollback.isPending}
              onClick={runPreview}
            >
              <Eye className="w-3.5 h-3.5 mr-1" />
              Preview rollback
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={rollback.isPending}
              onClick={() => { setConfirmRollback(true); }}
            >
              <Undo2 className="w-3.5 h-3.5 mr-1" />
              Roll back to baseline
            </Button>
          </div>
        ) : null}
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Config captured before the change starts and after it completes. Metrics and other state
        are reconstructable from history for the capture times.
      </p>
      <ul className="divide-y divide-border-subtle">
        {snapshots.map((d) => {
          const badge = statusBadge(d);
          const isOpen = expanded === d.deviceId;
          return (
            <li key={d.deviceId} className="py-2">
              <button
                type="button"
                className="w-full flex items-center justify-between gap-3 text-left"
                onClick={() => { setExpanded(isOpen ? null : d.deviceId); }}
              >
                <span className="flex items-center gap-1.5 min-w-0">
                  {isOpen ? (
                    <ChevronDown className="w-3.5 h-3.5 shrink-0" />
                  ) : (
                    <ChevronRight className="w-3.5 h-3.5 shrink-0" />
                  )}
                  <span className="text-sm font-medium truncate">{d.deviceName}</span>
                </span>
                <Badge className={cn('border-0 font-normal shrink-0', badge.className)}>{badge.label}</Badge>
              </button>
              {isOpen ? (
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(['pre', 'post'] as const).map((phase) => {
                    const snap = d[phase];
                    return (
                      <div key={phase} className="min-w-0">
                        <div className="text-xs font-semibold text-muted-foreground mb-1">
                          {phase === 'pre' ? 'Before' : 'After'}
                          {snap ? ` · ${new Date(snap.capturedAt).toLocaleString()}` : ''}
                        </div>
                        {snap?.configText ? (
                          <ConfigText
                            text={snap.configText}
                            className="text-[11px] leading-snug bg-muted/40 rounded p-2 overflow-x-auto max-h-72 whitespace-pre-wrap break-words"
                          />
                        ) : (
                          <p className="text-xs text-muted-foreground italic">
                            {snap?.note ?? 'Not captured yet.'}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      {rollback.isError ? (
        <p className="mt-3 text-xs text-sev-2">
          Could not roll back: {apiErrorMessage(rollback.error, 'the request failed')}
        </p>
      ) : null}
      {rollback.data && rollback.data.skipped.length > 0 ? (
        <div className="mt-3 text-xs">
          <p className="font-semibold">Not rolled back</p>
          <ul className="mt-1 space-y-0.5 text-muted-foreground">
            {rollback.data.skipped.map((s) => (
              <li key={s.deviceId}>
                {s.deviceName ?? s.deviceId}: {s.reason}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {restores && restores.length > 0 ? (
        <div className="mt-4 border-t border-border-subtle pt-3">
          <h3 className="text-xs font-semibold mb-1">Rollback restores</h3>
          <p className="text-xs text-muted-foreground mb-2">
            Each device's config is captured before and after its restore, and the restore is judged by
            what the device has afterwards. Both captures are in the device's config history.
          </p>
          <ul className="space-y-3">
            {restores.map((r) => (
              <li key={r.id} className="text-xs min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Link to={`/devices/${r.deviceId}`} className="font-medium text-primary hover:underline">
                    {r.deviceName ?? r.deviceId}
                  </Link>
                  <RestoreStatusBadge status={r.status} />
                  <RestoreTimes restore={r} />
                </div>
                {r.detail ? (
                  <ConfigText
                    text={r.detail}
                    className="mt-1 whitespace-pre-wrap break-words font-mono text-[11px] text-muted-foreground"
                  />
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <AppSheet
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        size="lg"
        title="Rollback preview"
        description="Nothing is changed on the devices: each shows what restoring it to its baseline would change, the same preview its own page gives."
      >
        <AppSheet.Body>
          {previewRollback.isPending ? (
            <p className="text-sm text-muted-foreground">Running dry-run on each device…</p>
          ) : preview && preview.length > 0 ? (
            <ul className="space-y-4">
              {preview.map((d) => (
                <li key={d.device_id} className="min-w-0">
                  <div className="text-sm font-medium mb-1">
                    {d.device_name ?? d.device_id}
                  </div>
                  {d.warnings ? <RestoreWarnings warnings={d.warnings} className="mb-1" /> : null}
                  {d.error ? (
                    <p className="text-xs text-sev-2">{d.error}</p>
                  ) : d.diff && d.diff.trim() !== '' ? (
                    <ConfigText
                      text={d.diff}
                      className="text-[11px] leading-snug bg-muted/40 rounded p-2 overflow-x-auto max-h-72 whitespace-pre-wrap break-words"
                    />
                  ) : (
                    <p className="text-xs text-muted-foreground italic">
                      No differences — the device already matches the baseline.
                    </p>
                  )}
                </li>
              ))}
            </ul>
          ) : previewRollback.isError ? (
            <p className="text-sm text-sev-2">Failed to run the rollback preview.</p>
          ) : (
            <p className="text-sm text-muted-foreground">
              No affected devices with a baseline snapshot to preview.
            </p>
          )}
        </AppSheet.Body>
      </AppSheet>

      <ConfirmationModal
        isActive={confirmRollback}
        title="Roll back to baseline?"
        message="Each affected device is restored to the config captured before the change started, the same restore its own page offers. Its config is captured first, so you can restore that from the device's page if you need to. A device that can't be restored is skipped, with the reason."
        confirmText="Roll back"
        isDanger
        isLoading={rollback.isPending}
        onCancel={() => { setConfirmRollback(false); }}
        onConfirm={() => {
          rollback.mutate(cmId, { onSettled: () => { setConfirmRollback(false); } });
        }}
      />
    </section>
  );
}
